import { Users, UserCheck, Store, Percent, PieChart, ListChecks } from 'lucide-react'
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { useOverviewAnalyticsDifare } from '../hooks/useAnalyticsDifare'
import { INDIGO, AZUL, VERDE, MORADO, ROSA, AMARILLO, KpiCard } from './KpiCard'
import { nf, fechaCorta } from '../format'

export function OverviewTab() {
  const { data, isLoading, error } = useOverviewAnalyticsDifare()

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-32">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500" />
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className="flex items-center justify-center py-32">
        <p className="text-destructive text-sm text-center max-w-md">
          No se pudo cargar el resumen. Verifique que la integración de Analytics Difare esté
          configurada y activa, e intente nuevamente.
        </p>
      </div>
    )
  }

  const serie = (data.registros_por_fecha ?? []).map((p) => ({
    fecha: fechaCorta(p.fecha),
    cantidad: p.cantidad,
  }))

  return (
    <div className="space-y-5">
      {/* KPIs principales */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <KpiCard
          icon={UserCheck}
          label="Dependientes"
          value={nf(data.dependientes_totales)}
          sub="Dependientes activos registrados"
          color={VERDE}
        />
        <KpiCard
          icon={ListChecks}
          label="Trivias completadas"
          value={nf(data.trivias_completadas)}
          sub="Trivias terminadas (todas las fases)"
          color={INDIGO}
        />
        <KpiCard
          icon={Percent}
          label="% participación"
          value={`${data.pct_participacion.toFixed(1)}%`}
          sub="Dependientes que completaron ÷ total"
          color={AZUL}
        />
        <KpiCard
          icon={PieChart}
          label="% faltante"
          value={`${data.pct_faltante.toFixed(1)}%`}
          sub="Dependientes que aún no completan"
          color={ROSA}
        />
        <KpiCard
          icon={Users}
          label="Visitadores"
          value={nf(data.visitadores_totales)}
          sub={`✓ ${nf(data.visitadores_verificados)} verificados · ${nf(
            data.visitadores_pendientes,
          )} pendientes`}
          color={AMARILLO}
        />
        <KpiCard
          icon={Store}
          label="Puntos de venta únicos"
          value={nf(data.puntos_venta_totales)}
          sub="Puntos de venta distintos"
          color={MORADO}
        />
      </div>

      {/* Registros de dependientes por fecha */}
      <Card className="border border-gray-300 dark:border-gray-700">
        <CardHeader>
          <CardTitle className="text-sm font-medium">Registros de dependientes por fecha</CardTitle>
        </CardHeader>
        <CardContent>
          {serie.length === 0 ? (
            <p className="text-sm text-muted-foreground py-8 text-center">
              Aún no hay registros para mostrar.
            </p>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={serie} margin={{ top: 8, right: 8, left: 0, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} opacity={0.3} />
                <XAxis dataKey="fecha" tick={{ fontSize: 11 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v: number) => [nf(v), 'Registros']} />
                <Bar dataKey="cantidad" fill={VERDE} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
